import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Brain, Loader2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { ReviewQualityTab } from "./ReviewQualityTab";
import { ReviewRiskTab } from "./ReviewRiskTab";

export function ContextualReviewPanel({ formData, reviewMode = "drafting", onApplySuggestion, onClose }) {
  const [reviewing, setReviewing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [expandedClause, setExpandedClause] = useState(null);
  const [activeTab, setActiveTab] = useState("quality");
  const [activeSection, setActiveSection] = useState("clauses");

  const [riskReviewing, setRiskReviewing] = useState(false);
  const [riskResult, setRiskResult] = useState(null);
  const pollRef = useRef(null);
  const riskPollRef = useRef(null);

  const startReview = async () => {
    setReviewing(true);
    setResult(null);
    setError(null);
    const contractText = formData.sections?.length > 0
      ? formData.sections.map(s => `## ${s.name}\n\n${s.content}`).join("\n\n---\n\n")
      : formData.content || "";
    if (!contractText || contractText.length < 50) {
      toast.error("Not enough content to review. Add at least a paragraph of contract text.");
      setReviewing(false);
      return;
    }
    const payload = {
      contract_text: contractText, review_mode: reviewMode,
      contract_type: formData.contract_type || "", jurisdiction: formData.jurisdiction || "",
    };

    // Quality review
    try {
      const res = await apiClient.post("/contextual-review", payload);
      const taskId = res.data.task_id;
      pollRef.current = setInterval(async () => {
        try {
          const poll = await apiClient.get(`/contextual-review/${taskId}`);
          if (poll.data.status === "completed" && poll.data.result) {
            clearInterval(pollRef.current);
            setResult(poll.data.result);
            setReviewing(false);
          } else if (poll.data.status === "failed") {
            clearInterval(pollRef.current);
            setError(poll.data.error || "Quality review failed");
            setReviewing(false);
          }
        } catch { clearInterval(pollRef.current); setError("Lost connection"); setReviewing(false); }
      }, 4000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start review");
      setReviewing(false);
    }

    // Risk review (parallel)
    setRiskReviewing(true);
    setRiskResult(null);
    try {
      const riskRes = await apiClient.post("/ai/risk-review", {
        contract_text: contractText, contract_type: formData.contract_type || "",
        jurisdiction: formData.jurisdiction || "", review_mode: reviewMode,
      });
      const riskTaskId = riskRes.data.task_id;
      riskPollRef.current = setInterval(async () => {
        try {
          const poll = await apiClient.get(`/ai/risk-review/${riskTaskId}`);
          if (poll.data.status === "completed" && poll.data.result) {
            clearInterval(riskPollRef.current);
            setRiskResult(poll.data.result);
            setRiskReviewing(false);
          } else if (poll.data.status === "failed") {
            clearInterval(riskPollRef.current);
            setRiskReviewing(false);
          }
        } catch { clearInterval(riskPollRef.current); setRiskReviewing(false); }
      }, 4000);
    } catch { setRiskReviewing(false); }
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (riskPollRef.current) clearInterval(riskPollRef.current);
    };
  }, []);

  return (
    <div className="border-l bg-white dark:bg-slate-900 flex flex-col h-full" data-testid="contextual-review-panel">
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Brain size={18} className="text-violet-500" />
          <span className="text-sm font-semibold">AI Contract Intelligence</span>
          <Badge className="rounded-sm text-[10px] bg-violet-100 text-violet-700">
            {reviewMode === "third_party_review" ? "Buyer Protection" : "Draft Review"}
          </Badge>
        </div>
        {onClose && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}><X size={14} /></Button>}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {/* Start screen */}
          {!result && !riskResult && !reviewing && !riskReviewing && (
            <div className="text-center py-6" data-testid="review-start">
              <Brain size={40} className="mx-auto text-violet-300 mb-3" />
              <p className="text-sm font-medium text-slate-700 mb-1">
                {reviewMode === "third_party_review" ? "Analyze Supplier Contract" : "Review Your Draft"}
              </p>
              <p className="text-xs text-slate-400 mb-4 max-w-xs mx-auto">
                AI will run two analyses in parallel — a quality review and a risk assessment.
              </p>
              {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
              <Button onClick={startReview} className="gap-2 rounded-sm bg-violet-600 hover:bg-violet-700" data-testid="start-contextual-review">
                <Brain size={14} /> Run AI Review
              </Button>
            </div>
          )}

          {/* Loading */}
          {(reviewing || riskReviewing) && !result && !riskResult && (
            <div className="text-center py-8" data-testid="review-loading">
              <Loader2 size={32} className="mx-auto text-violet-400 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-600">Analyzing contract...</p>
              <p className="text-xs text-slate-400 mt-1">Running quality review and risk assessment in parallel</p>
            </div>
          )}

          {/* Results tabs */}
          {(result || riskResult) && (
            <>
              <div className="flex gap-1 border-b pb-1 mb-1">
                <button onClick={() => setActiveTab("quality")}
                  className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${activeTab === "quality" ? "bg-violet-100 text-violet-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}
                  data-testid="tab-quality">
                  Quality Review {result ? `(${result.overall_score}/10)` : reviewing ? "..." : ""}
                </button>
                <button onClick={() => setActiveTab("risk")}
                  className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${activeTab === "risk" ? "bg-amber-100 text-amber-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}
                  data-testid="tab-risk">
                  Risk Assessment {riskResult ? `(${riskResult.overall_risk_score}/10)` : riskReviewing ? "..." : ""}
                </button>
              </div>

              {activeTab === "quality" && (
                <>
                  {reviewing && (
                    <div className="text-center py-6"><Loader2 size={24} className="mx-auto text-violet-400 animate-spin mb-2" /><p className="text-xs text-slate-500">Analyzing drafting quality...</p></div>
                  )}
                  {result && (
                    <ReviewQualityTab result={result} expandedClause={expandedClause} setExpandedClause={setExpandedClause}
                      activeSection={activeSection} setActiveSection={setActiveSection} onApplySuggestion={onApplySuggestion} onRerun={startReview} />
                  )}
                </>
              )}

              {activeTab === "risk" && (
                <>
                  {riskReviewing && (
                    <div className="text-center py-6"><Loader2 size={24} className="mx-auto text-amber-400 animate-spin mb-2" /><p className="text-xs text-slate-500">Analyzing risk exposure...</p></div>
                  )}
                  {riskResult && <ReviewRiskTab riskResult={riskResult} onApplySuggestion={onApplySuggestion} />}
                  {!riskReviewing && !riskResult && <p className="text-xs text-slate-400 text-center py-4">Risk review not yet run. Click "Re-run Review" below.</p>}
                </>
              )}

              <Button onClick={startReview} variant="outline" size="sm" className="w-full gap-1.5 rounded-sm text-xs mt-2" data-testid="rerun-review">
                <Brain size={12} /> Re-run Review
              </Button>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
